import { defineStore } from 'pinia'
import { useSupabase } from './supabase'
import { useLocalStorage } from '@vueuse/core'
import { computed } from 'vue'

interface CommonNotificationFields {
  object_id: string
  from: {
    username: string
    avatar: string
  }
  read: boolean
  created_at: string
}

export interface TrophyNotification extends CommonNotificationFields {
  type: 'trophy_earned'
  trophy_type: 'FIRST' | 'SECOND' | 'THIRD'
  word: string
  word_id: string
  photo: {
    id: string
    url: string
    description: string
  }
}

export interface GroupTransitionNotification extends CommonNotificationFields {
  type: 'group_transition'
  group_id: string
  word: string
  word_id: string
}

export interface NewLikeNotification extends CommonNotificationFields {
  type: 'new_like'
  like_count: number
}

export interface FriendInviteReceivedNotification extends CommonNotificationFields {
  type: 'friend_request_received'
}

export interface FriendInviteAcceptedNotification extends CommonNotificationFields {
  type: 'friend_request_accepted'
}

export interface NewCommentNotification extends CommonNotificationFields {
  type: 'new_comment'
  photo: {
    id: string
    url: string
    description: string
    photo_owner_id: string
  }
  word: string
  word_id: string
  comment_content: string
}

export type Notification =
  | TrophyNotification
  | GroupTransitionNotification
  | NewLikeNotification
  | FriendInviteReceivedNotification
  | FriendInviteAcceptedNotification
  | NewCommentNotification

export type DbNotification = {
  id: string
  user_id: string
  actor_id: string
  object_id: string
  type:
    | 'trophy_earned'
    | 'group_transition'
    | 'new_like'
    | 'friend_request_received'
    | 'friend_request_accepted'
    | 'new_comment'
  read: boolean
  created_at: string
  username: string
  avatar: string
  trophy_type?: string
  photo_id?: string
  photo_url?: string
  photo_description?: string
  word?: string
  word_id?: string
  like_count?: number
  comment_content?: string
  photo_owner_id?: string
}

export const useNotifications = defineStore('notification', () => {
  const supabase = useSupabase()
  const notifications = useLocalStorage<Notification[]>('notifications', [])
  const formatted = computed(() => {
    /**
     * The goal of this is to format the notifications into a more readable format.
     * This includes:
     * - Grouping by type
     * - Grouping by object_id
     * - Sorting by date
     * - Formatting the date
     * - Adding a link to the object
     */

    const grouped = notifications.value.reduce((acc, notification) => {
      const key = `${notification.type}-${notification.object_id}`
      if (!acc[key]) {
        acc[key] = []
      }
      acc[key].push(notification)
      // Sort by date
      acc[key].sort((a, b) => {
        const dateA = new Date(a.created_at)
        const dateB = new Date(b.created_at)
        return dateB.getTime() - dateA.getTime()
      })
      return acc
    }, {} as Record<string, typeof notifications.value>)

    return [...Object.entries(grouped)].toSorted(
      (a: [string, typeof notifications.value], b: [string, typeof notifications.value]) => {
        const dateA = new Date(a[1][0].created_at)
        const dateB = new Date(b[1][0].created_at)
        return dateB.getTime() - dateA.getTime()
      }
    )
  })
  const unread = computed(() => notifications.value.filter((n) => !n.read).length)

  function reset() {
    notifications.value = []
  }

  function transformDbNotification(notification: DbNotification): Notification {
    const baseNotification = {
      object_id: notification.object_id,
      from: {
        username: notification.username,
        avatar: notification.avatar
      },
      read: notification.read,
      created_at: notification.created_at
    }

    switch (notification.type) {
      case 'trophy_earned':
        return {
          ...baseNotification,
          type: 'trophy_earned' as const,
          trophy_type: notification.trophy_type as 'FIRST' | 'SECOND' | 'THIRD',
          word: notification.word!,
          word_id: notification.word_id!,
          photo: {
            id: notification.photo_id!,
            url: notification.photo_url!,
            description: notification.photo_description!
          }
        }
      case 'group_transition':
        return {
          ...baseNotification,
          type: 'group_transition' as const,
          group_id: notification.object_id,
          word: notification.word!,
          word_id: notification.word_id!
        }
      case 'new_like':
        return {
          ...baseNotification,
          type: 'new_like' as const,
          like_count: notification.like_count!
        }
      case 'friend_request_received':
        return {
          ...baseNotification,
          type: 'friend_request_received' as const
        }
      case 'friend_request_accepted':
        return {
          ...baseNotification,
          type: 'friend_request_accepted' as const
        }
      case 'new_comment':
        return {
          ...baseNotification,
          type: 'new_comment' as const,
          photo: {
            id: notification.photo_id!,
            url: notification.photo_url!,
            description: notification.photo_description!,
            photo_owner_id: notification.photo_owner_id!
          },
          word: notification.word!,
          word_id: notification.word_id!,
          comment_content: notification.comment_content || ''
        }
      default:
        // This should ideally not happen if types are correct
        // Return a default or throw an error
        console.error('Unknown notification type:', notification)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return notification as any // Or handle error appropriately
    }
  }

  async function fetchNotifications() {
    if (supabase.user.status === 'SignedIn') {
      const { data, error } = await supabase.api
        .from('notification_details')
        .select('*')
        .eq('user_id', supabase.user.data.id)
        .order('created_at', { ascending: false })

      if (error) {
        console.error(error)
        throw error
      } else {
        notifications.value = data.map(transformDbNotification)
      }
    }
  }

  function addNotification(payload: DbNotification) {
    // Check if notification already exists (optional, based on specific needs)
    // if (notifications.value.some(n => n.id === payload.id)) {
    //   return;
    // }
    const newNotification = transformDbNotification(payload)
    notifications.value.unshift(newNotification) // Add to the beginning
  }

  async function markNotificationsRead() {
    if (supabase.user.status === 'SignedIn') {
      const unreadIds = notifications.value.filter((n) => !n.read).map((n) => n.object_id) // Assuming object_id relates to the base notification id - check this assumption
      if (unreadIds.length === 0) return

      const { error } = await supabase.api
        .from('notifications') // Target the base notifications table
        .update({ read: true })
        .eq('user_id', supabase.user.data.id)

      if (error) {
        console.error(error)
      } else {
        notifications.value.forEach((n) => {
          if (!n.read) {
            n.read = true
          }
        })
      }
    }
  }

  return {
    notifications,
    formatted,
    unread,
    fetchNotifications,
    markNotificationsRead,
    addNotification,
    reset
  }
})
